import React, { useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';

function trMoney(v) {
  const n = Number(v || 0);
  return `₺${n.toLocaleString('tr-TR')}`;
}

function clampPct(n) {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

export default function MonthPaymentsModal({
  open,
  onClose,
  items = [],
  totalTry = 0,
  monthFrom,
  monthTo,
  onSelectMember,
}) {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const moneySx = {
    color: 'success.main',
    fontWeight: 700,
  };
  const [q, setQ] = useState('');
  const [sortBy, setSortBy] = useState('totalTry'); // totalTry | paymentCount | fullName
  const [sortDir, setSortDir] = useState('desc'); // asc | desc

  const headerSubtitle = useMemo(() => {
    const range = monthFrom && monthTo ? `${monthFrom} → ${monthTo}` : '';
    return range ? `Dönem: ${range}` : 'Bu ay yapılan tahsilatlar';
  }, [monthFrom, monthTo]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    let arr = Array.isArray(items) ? items : [];

    if (qq) {
      arr = arr.filter((x) =>
        String(x.fullName || '')
          .toLowerCase()
          .includes(qq)
      );
    }

    const dir = sortDir === 'asc' ? 1 : -1;

    return [...arr].sort((a, b) => {
      if (sortBy === 'fullName') {
        const A = String(a.fullName || '');
        const B = String(b.fullName || '');
        return A.localeCompare(B, 'tr') * dir;
      }
      if (sortBy === 'paymentCount') {
        return (Number(a.paymentCount || 0) - Number(b.paymentCount || 0)) * dir;
      }
      return (Number(a.totalTry || 0) - Number(b.totalTry || 0)) * dir;
    });
  }, [items, q, sortBy, sortDir]);

  const listTotalTry = useMemo(
    () => filtered.reduce((s, x) => s + Number(x.totalTry || 0), 0),
    [filtered]
  );

  const SortButton = ({ id, label }) => {
    const active = sortBy === id;
    const dirLabel = active ? (sortDir === 'asc' ? '↑' : '↓') : '';
    return (
      <MDButton
        size="small"
        variant={active ? 'gradient' : 'outlined'}
        color={active ? 'dark' : 'dark'}
        onClick={() => {
          if (sortBy !== id) {
            setSortBy(id);
            setSortDir(id === 'fullName' ? 'asc' : 'desc');
          } else {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
          }
        }}
        sx={{ px: 1.25, minWidth: 0 }}
      >
        {label} {dirLabel}
      </MDButton>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={isMobile}>
      <DialogTitle sx={{ pr: 6, pb: 1.5 }}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDBox>
            <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.15 }}>
              Bu Ay Tahsilatlar
            </MDTypography>
            <MDTypography variant="button" color="text" sx={{ opacity: 0.7, lineHeight: 1.2 }}>
              {headerSubtitle}
            </MDTypography>
          </MDBox>

          <IconButton onClick={onClose} size="small" aria-label="close">
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {/* Top summary */}
        <MDBox
          mb={2}
          p={2}
          borderRadius="lg"
          sx={(t) => ({ border: `1px solid ${t.palette.divider}` })}
        >
          <MDBox
            display="flex"
            flexDirection={isMobile ? 'column' : 'row'}
            alignItems={isMobile ? 'stretch' : 'center'}
            justifyContent="space-between"
            gap={isMobile ? 1.5 : 2}
          >
            <MDBox>
              <MDTypography variant="button" color="text" sx={{ opacity: 0.7 }}>
                Toplam (dashboard)
              </MDTypography>
              <MDTypography variant="h5" sx={{ ...moneySx, lineHeight: 1.1 }}>
                {trMoney(totalTry)}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>

        {/* Search */}
        <MDBox
          mb={1.5}
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          gap={1}
          alignItems={isMobile ? 'stretch' : 'center'}
        >
          <MDInput
            placeholder="Üye ara…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            fullWidth
          />
          {q && (
            <MDButton variant="outlined" color="dark" onClick={() => setQ('')}>
              Temizle
            </MDButton>
          )}
        </MDBox>

        {/* Sort chips (same on mobile & desktop) */}
        <MDBox mb={2} display="flex" gap={1} flexWrap="wrap">
          <SortButton id="totalTry" label="Tutar" />
          <SortButton id="paymentCount" label="Tahsilat" />
          <SortButton id="fullName" label="İsim" />
        </MDBox>

        {/* Card list (desktop 2 columns, mobile 1 column) */}
        <MDBox display="grid" gridTemplateColumns={isMobile ? '1fr' : '1fr 1fr'} gap={1.25}>
          {filtered.length === 0 ? (
            <MDBox
              p={2}
              borderRadius="lg"
              sx={(t) => ({
                gridColumn: '1 / -1',
                border: `1px solid ${t.palette.divider}`,
              })}
            >
              <MDTypography variant="button" color="text" sx={{ opacity: 0.7 }}>
                Kayıt bulunamadı.
              </MDTypography>
            </MDBox>
          ) : (
            filtered.map((x) => {
              const pct = clampPct((Number(x.totalTry || 0) / Number(totalTry || 1)) * 100);

              return (
                <MDBox
                  key={x.memberId}
                  p={1.5}
                  borderRadius="lg"
                  onClick={() => onSelectMember?.(x)}
                  sx={(t) => ({
                    border: `1px solid ${t.palette.divider}`,
                    cursor: onSelectMember ? 'pointer' : 'default',
                    transition: 'transform 120ms ease, box-shadow 120ms ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: t.boxShadows?.md ?? '0 6px 18px rgba(0,0,0,0.08)',
                    },
                  })}
                >
                  {/* Top row: name + amount */}
                  <MDBox
                    display="flex"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    gap={1}
                  >
                    <MDBox sx={{ minWidth: 0 }}>
                      <MDTypography variant="button" fontWeight="medium" sx={{ lineHeight: 1.2 }}>
                        {x.fullName}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
                        Tahsilat adedi: {Number(x.paymentCount || 0)}
                      </MDTypography>
                    </MDBox>

                    <MDBox textAlign="right">
                      <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
                        Pay: {pct.toFixed(1)}%
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  {/* Bottom mini stats */}
                  <MDBox mt={1.25} display="flex" justifyContent="space-between" gap={1}>
                    <MDBox>
                      <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
                        Tutar
                      </MDTypography>
                      <MDTypography variant="button" sx={{ ...moneySx, lineHeight: 1.2 }}>
                        {trMoney(x.totalTry)}
                      </MDTypography>
                    </MDBox>

                    <MDBox textAlign="right">
                      <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
                        Tahsilat
                      </MDTypography>
                      <MDTypography variant="button" sx={{ lineHeight: 1.2 }}>
                        {Number(x.paymentCount || 0)} kez
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MDBox>
              );
            })
          )}
        </MDBox>

        <MDTypography variant="caption" color="text" sx={{ opacity: 0.7, mt: 1, display: 'block' }}>
          Not: Kart’a tıklamayı bir sonraki adımda “üye ödeme detayına” bağlayacağız.
        </MDTypography>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <MDButton variant="gradient" color="dark" onClick={onClose} fullWidth={isMobile}>
          Kapat
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
